import React, { useState, useEffect } from 'react';
import apiService from '../../services/api.service';
import './Terms.css';

const Terms = () => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getAdminTerms();
      console.log('Full API response:', response);
      console.log('response.data:', response.data);
      
      if (response.data) {
        // Try different possible response structures
        if (response.data.terms) {
          console.log('Found terms at response.data.terms:', response.data.terms);
          setTerms(response.data.terms);
        } else if (response.data.data && response.data.data.terms) {
          console.log('Found terms at response.data.data.terms:', response.data.data.terms);
          setTerms(response.data.data.terms);
        } else if (Array.isArray(response.data)) {
          console.log('response.data is array:', response.data);
          setTerms(response.data);
        } else {
          console.log('Unexpected response structure');
          setError('Unexpected API response structure');
        }
      }
    } catch (err) {
      setError('Failed to load terms: ' + (err.response?.data?.error || err.message));
      console.error('Error fetching terms:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      
      const response = await apiService.updateTerms(terms);
      if (response.data.success) {
        setSuccessMessage('Terms updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError('Failed to save terms: ' + (err.response?.data?.error || err.message));
      console.error('Error saving terms:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTerm = () => {
    setTerms([...terms, '']);
    setEditingIndex(terms.length);
    setEditText('');
  };

  const handleDeleteTerm = (index) => {
    if (window.confirm('Are you sure you want to delete this term?')) {
      const newTerms = terms.filter((_, i) => i !== index);
      setTerms(newTerms);
    }
  };

  const handleEditTerm = (index) => {
    setEditingIndex(index);
    setEditText(terms[index]);
  };

  const handleSaveTerm = (index) => {
    if (editText.trim()) {
      const newTerms = [...terms];
      newTerms[index] = editText.trim();
      setTerms(newTerms);
    }
    setEditingIndex(null);
    setEditText('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditText('');
  };

  const handleMoveTerm = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= terms.length) return;
    
    const newTerms = [...terms];
    [newTerms[index], newTerms[newIndex]] = [newTerms[newIndex], newTerms[index]];
    setTerms(newTerms);
  };

  if (loading) {
    return (
      <div className="terms-management">
        <div className="loading">Loading terms...</div>
      </div>
    );
  }

  return (
    <div className="terms-management">
      <div className="page-header-admin">
        <h2>Terms & Conditions Management</h2>
        <p>Manage the terms and conditions displayed on the visitor registration page</p>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
        </div>
      )}

      <div className="terms-container">
        <div className="terms-header">
          <h3>Terms List ({terms.length} items)</h3>
          <div className="terms-actions">
            <button 
              className="btn btn-primary" 
              onClick={handleAddTerm}
              disabled={saving}
            >
              <i className="fas fa-plus"></i> Add New Term
            </button>
            <button 
              className="btn btn-success" 
              onClick={handleSave}
              disabled={saving || terms.length === 0}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Save All Changes
                </>
              )}
            </button>
          </div>
        </div>

        <div className="terms-list">
          {terms.length === 0 ? (
            <div className="no-terms">
              <p>No terms added yet. Click "Add New Term" to get started.</p>
            </div>
          ) : (
            terms.map((term, index) => (
              <div key={index} className="term-item">
                <div className="term-number">{index + 1}.</div>
                
                {editingIndex === index ? (
                  <div className="term-edit">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="term-textarea"
                      rows="3"
                      autoFocus
                    />
                    <div className="term-edit-actions">
                      <button 
                        className="btn btn-sm btn-success"
                        onClick={() => handleSaveTerm(index)}
                        disabled={!editText.trim()}
                      >
                        <i className="fas fa-check"></i> Save
                      </button>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={handleCancelEdit}
                      >
                        <i className="fas fa-times"></i> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="term-content">{term}</div>
                    <div className="term-actions">
                      <button 
                        className="btn btn-sm btn-icon"
                        onClick={() => handleMoveTerm(index, 'up')}
                        disabled={index === 0}
                        title="Move up"
                      >
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button 
                        className="btn btn-sm btn-icon"
                        onClick={() => handleMoveTerm(index, 'down')}
                        disabled={index === terms.length - 1}
                        title="Move down"
                      >
                        <i className="fas fa-arrow-down"></i>
                      </button>
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEditTerm(index)}
                        title="Edit"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteTerm(index)}
                        title="Delete"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Terms;
